import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

// Track actual scroll position, independently of sticky-header visibility state.
let surfaceFrame = 0;
function updateHeaderSurface() {
  surfaceFrame = 0;
  const header = document.getElementById('header-component');
  if (!header) return;
  const wrapper = document.querySelector('.page-wrapper');
  const scrollTop = Math.max(0, window.scrollY, wrapper?.scrollTop || 0);
  // Ease by distance in both directions, not a full appearance switch at 8px.
  // Clamp elastic overscroll and finish the blend within the first 160px.
  const distance = Math.min(1, scrollTop / 160);
  const glassProgress = distance * distance * (3 - 2 * distance);
  header.style.setProperty('--ws-header-solid', String(1 - glassProgress));
  header.style.setProperty('--ws-header-edge', String(.08 * glassProgress));
  header.toggleAttribute('data-ws-scrolled', scrollTop > 8);
}
function scheduleHeaderSurface() {
  if (!surfaceFrame) surfaceFrame = requestAnimationFrame(updateHeaderSurface);
}
document.addEventListener('scroll', scheduleHeaderSurface, { capture: true, passive: true });
window.addEventListener('resize', scheduleHeaderSurface, { passive: true });
window.addEventListener('pageshow', scheduleHeaderSurface);
document.addEventListener('shopify:section:load', scheduleHeaderSurface);
updateHeaderSurface();

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #motionVersions = new WeakMap();

  // Only the latest transition may change focus or remove the open attribute.
  #afterMotion(details, drawer, callback) {
    const version = (this.#motionVersions.get(details) || 0) + 1;
    this.#motionVersions.set(details, version);
    requestAnimationFrame(() => {
      if (this.#motionVersions.get(details) !== version) return;
      onAnimationEnd(drawer || details, () => {
        if (this.isConnected && this.#motionVersions.get(details) === version) callback();
      }, { subtree: false });
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    // Native summary toggling would hide details before its closing motion finishes.
    event?.preventDefault();
    return this.refs.details.querySelector('summary')?.getAttribute('aria-expanded') === 'true'
      ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.open = true;

    this.preventInitialAccordionAnimations(details);
    // Establish the closed position before starting the transition, including Safari.
    requestAnimationFrame(() => {
      if (summary.getAttribute('aria-expanded') !== 'true') return;
      const openingDrawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      openingDrawer?.getBoundingClientRect();
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      this.#afterMotion(details, drawer, () => {
        if (details.classList.contains('menu-open')) trapFocus(details);
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    this.#afterMotion(
      details, drawer,
      () => {
        if (summary.getAttribute('aria-expanded') === 'true') return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          summary.focus({ preventScroll: true });
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
